/** @format */

import React from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    alpha,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { colors } from "../../utility/types/constant";
import { Configuration } from "../../utility/types/circleType";
import {
    WEEK_DAYS,
    formatContribution,
    formatDate,
    formatPayOutDay,
    getCircleCountryInfo,
    getCurrencyCode,
    getFrequency,
    getSizeLabel,
    normalizeDays,
} from "./circleDisplay";
import {
    CountryEligibility,
    getCountryEligibilityMessage,
} from "../../utility/circleCountryEligibility";

interface Leader {
    _id?: string;
    firstName?: string;
    lastName?: string;
    profileImage?: string;
}

interface CircleDiscoveryCardProps {
    _id: string;
    circleName: string;
    circleImage?: string;
    leader?: Leader | null;
    configuration?: Partial<Configuration> | null;
    membersCount?: string;
    totalMembers: number;
    isMember?: boolean;
    isPendingJoinRequest?: boolean;
    hasActiveCycle?: boolean;
    inJoinWindow?: boolean;
    joinWindowMessage?: string;
    activeCycleInfo?: { startDate?: string; endDate?: string } | null;
    /** A cycle exists but hasn't started yet, so joining is still open. */
    cycleStartsLater?: boolean;
    joinButtonDisabled?: boolean;
    countryEligibility?: CountryEligibility | null;
    onJoinButtonClick: (id: string) => void;
    onViewButtonClick: (id: string) => void;
    onMoreIconClick: (id: string) => void;
}

type Status = "member" | "pending" | "joinWindow" | "locked" | "open";

const STATUS_STYLE: Record<Status, { label: string; color: string }> = {
    member: { label: "You're a member", color: colors.mainSecondary },
    pending: { label: "Request pending", color: "#ff9800" },
    joinWindow: { label: "Join window open", color: colors.mainSecondary },
    locked: { label: "Cycle in progress", color: "#ef5350" },
    open: { label: "Open to join", color: colors.mainSecondary },
};

const glass = {
    backgroundColor: "rgba(4, 13, 69, 0.55)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    color: colors.white,
    border: "1px solid rgba(255,255,255,0.18)",
};

const CircleDiscoveryCard = (props: CircleDiscoveryCardProps) => {
    const {
        _id,
        circleName,
        circleImage,
        leader,
        configuration,
        membersCount,
        totalMembers,
        isMember = false,
        isPendingJoinRequest = false,
        hasActiveCycle = false,
        inJoinWindow = false,
        joinWindowMessage,
        activeCycleInfo,
        cycleStartsLater = false,
        joinButtonDisabled = false,
        countryEligibility = null,
        onJoinButtonClick,
        onViewButtonClick,
        onMoreIconClick,
    } = props;

    // Same priority as the previous overlays: pending > join window > active cycle.
    const status: Status =
        isMember ? "member"
            : isPendingJoinRequest ? "pending"
                : hasActiveCycle && inJoinWindow ? "joinWindow"
                    : hasActiveCycle ? "locked"
                        : "open";

    const statusStyle = STATUS_STYLE[status];
    const amount = formatContribution(
        configuration?.contributionAmount,
        configuration?.currency,
    );
    const currencyCode = getCurrencyCode(configuration?.currency);
    const frequency = getFrequency(configuration?.contributionPeriod);
    const activeDays = normalizeDays(configuration?.contributionDays);
    const country = getCircleCountryInfo(configuration?.country);
    const sizeLabel = getSizeLabel(membersCount);
    const payOutDayLabel = formatPayOutDay(configuration?.payOutDay);
    const cycleEnds = formatDate(activeCycleInfo?.endDate);
    const cycleStarts = formatDate(activeCycleInfo?.startDate);
    const coverUrl =
        typeof circleImage === "string" && circleImage.trim() ?
            circleImage.trim().replace(/["\\\n\r]/g, encodeURIComponent)
            : "";
    const leaderName = [
        leader?.firstName,
        leader?.lastName ? `${leader.lastName.charAt(0)}.` : "",
    ]
        .filter(Boolean)
        .join(" ");

    const statusNote =
        status === "locked" ?
            [joinWindowMessage, cycleEnds && `Cycle ends ${cycleEnds}`]
                .filter(Boolean)
                .join(" · ")
            : status === "joinWindow" ? joinWindowMessage
                : status === "pending" ? "Waiting for the circle leader to approve."
                    : status === "open" && cycleStartsLater && cycleStarts ?
                        `Cycle starts ${cycleStarts} · Join before then to be in the payout rotation.`
                        : "";

    // Informational only: the server enforces the same rule, so say so up front.
    const showCountryNote =
        !isMember && !isPendingJoinRequest && countryEligibility?.eligible === false;
    const countryNote =
        !showCountryNote || !countryEligibility ? ""
            : countryEligibility.reason === "country_mismatch" && country ?
                `Only open to members based in ${country.flag} ${country.name}.`
                : getCountryEligibilityMessage(countryEligibility);

    const stats = [
        {
            icon: <GroupsRoundedIcon />,
            label: "Members",
            value: String(totalMembers ?? 0),
            hint: sizeLabel ? `Size: ${sizeLabel}` : "",
        },
        {
            icon: <PaymentsRoundedIcon />,
            label: "Payout",
            value: configuration?.payOutPeriod || "—",
            hint: payOutDayLabel,
        },
        {
            icon: <PublicRoundedIcon />,
            label: "Country",
            value: country ? `${country.flag} ${country.code}` : "—",
            hint: country?.name ?? "",
        },
    ];

    return (
        <Grid item xs={12} sm={6} lg={4}>
            <Card
                component="article"
                aria-label={circleName}
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "20px",
                    overflow: "hidden",
                    border: `1px solid ${alpha(colors.primary, 0.08)}`,
                    boxShadow: `0 4px 18px ${alpha(colors.primary, 0.06)}`,
                    transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
                    "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 18px 40px ${alpha(colors.primary, 0.14)}`,
                        borderColor: alpha(colors.mainSecondary, 0.45),
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                        "&:hover": { transform: "none" },
                    },
                }}>
                <Box
                    sx={{
                        position: "relative",
                        height: 136,
                        flexShrink: 0,
                        bgcolor: colors.primary,
                        // Gradient stays visible if the cover image is missing or fails to load.
                        backgroundImage: [
                            coverUrl && `url("${coverUrl}")`,
                            `linear-gradient(135deg, ${colors.primary} 0%, #0a2159 60%, ${alpha(colors.mainSecondary, 0.7)} 140%)`,
                        ]
                            .filter(Boolean)
                            .join(", "),
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(180deg, rgba(4,13,69,0.15) 0%, rgba(4,13,69,0.78) 100%)",
                        },
                    }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ position: "absolute", inset: 0, p: 1.75, zIndex: 1 }}>
                        <Chip
                            size="small"
                            icon={
                                <Box
                                    component="span"
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        ml: "8px !important",
                                        borderRadius: "50%",
                                        bgcolor: statusStyle.color,
                                    }}
                                />
                            }
                            label={statusStyle.label}
                            sx={{ ...glass, fontWeight: 700, fontSize: "0.7rem", height: 26 }}
                        />
                        <Tooltip title="Circle details">
                            <IconButton
                                size="small"
                                aria-label={`More details about ${circleName}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMoreIconClick(_id);
                                }}
                                sx={{ ...glass, "&:hover": { bgcolor: "rgba(4,13,69,0.8)" } }}>
                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Typography
                        component="h3"
                        sx={{
                            position: "absolute",
                            left: 18,
                            right: 18,
                            bottom: 14,
                            zIndex: 1,
                            color: colors.white,
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                            fontSize: "1.15rem",
                            lineHeight: 1.25,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            textShadow: "0 2px 10px rgba(0,0,0,0.35)",
                        }}>
                        {circleName}
                    </Typography>
                </Box>

                <Stack spacing={2} sx={{ p: 2.25, flexGrow: 1 }}>
                    {leaderName && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                                src={leader?.profileImage}
                                alt={leaderName}
                                sx={{ width: 24, height: 24, fontSize: "0.75rem" }}>
                                {leader?.firstName?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                Led by{" "}
                                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                                    {leaderName}
                                </Box>
                            </Typography>
                        </Stack>
                    )}

                    <Box
                        sx={{
                            p: 1.75,
                            borderRadius: "14px",
                            bgcolor: alpha(colors.mainSecondary, 0.07),
                            border: `1px solid ${alpha(colors.mainSecondary, 0.2)}`,
                        }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            spacing={1}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: "0.02em" }}>
                                    Each contribution
                                </Typography>
                                <Stack direction="row" alignItems="baseline" flexWrap="wrap" columnGap={0.75}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 800,
                                            fontSize: "1.55rem",
                                            lineHeight: 1.15,
                                            color: colors.primary,
                                            letterSpacing: "-0.02em",
                                        }}>
                                        {amount}
                                    </Typography>
                                    {!amount.includes(currencyCode) && (
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                                            {currencyCode}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>

                            <Box sx={{ textAlign: "right", flexShrink: 0, ml: "auto" }}>
                                <Chip
                                    size="small"
                                    icon={<EventRepeatRoundedIcon sx={{ fontSize: "16px !important" }} />}
                                    label={frequency.label}
                                    sx={{
                                        fontWeight: 700,
                                        color: colors.white,
                                        bgcolor: colors.primary,
                                        "& .MuiChip-icon": { color: colors.white },
                                    }}
                                />
                                {frequency.detail && (
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        sx={{ mt: 0.5, color: "text.secondary" }}>
                                        {frequency.detail}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography
                            variant="caption"
                            component="div"
                            sx={{ mb: 0.75, color: "text.secondary", fontWeight: 600 }}>
                            Contribution days
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={0.6}
                            role="img"
                            aria-label={
                                activeDays.length ?
                                    `Contributes on ${activeDays.join(", ")}`
                                    : "No contribution days set"
                            }>
                            {WEEK_DAYS.map((day) => {
                                const on = activeDays.includes(day);
                                return (
                                    <Tooltip key={day} title={on ? `Contributes on ${day}` : day}>
                                        <Box
                                            aria-hidden
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                height: 30,
                                                maxWidth: 40,
                                                borderRadius: "9px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.72rem",
                                                fontWeight: 700,
                                                transition: "all 0.2s ease",
                                                color: on ? colors.white : alpha(colors.primary, 0.35),
                                                bgcolor: on ? colors.mainSecondary : alpha(colors.primary, 0.04),
                                                border: `1px solid ${on ? colors.mainSecondary : alpha(colors.primary, 0.08)}`,
                                                boxShadow: on ? `0 4px 10px ${alpha(colors.mainSecondary, 0.3)}` : "none",
                                            }}>
                                            {day.slice(0, 2)}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    </Box>

                    <Stack
                        direction="row"
                        sx={{
                            borderTop: `1px dashed ${alpha(colors.primary, 0.12)}`,
                            pt: 1.75,
                        }}>
                        {stats.map((stat, i) => (
                            <Box
                                key={stat.label}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    px: 1,
                                    "&:first-of-type": { pl: 0 },
                                    borderLeft: i === 0 ? "none" : `1px solid ${alpha(colors.primary, 0.08)}`,
                                }}>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary" }}>
                                    {React.cloneElement(stat.icon, { sx: { fontSize: 15 } })}
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        {stat.label}
                                    </Typography>
                                </Stack>
                                <Typography
                                    noWrap
                                    sx={{ mt: 0.25, fontWeight: 800, fontSize: "0.95rem", color: colors.primary }}>
                                    {stat.value}
                                </Typography>
                                {stat.hint && (
                                    <Typography noWrap variant="caption" component="div" sx={{ color: "text.disabled" }}>
                                        {stat.hint}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Stack>

                <Box sx={{ px: 2.25, pb: 2.25 }}>
                    {statusNote && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{
                                mb: 1.25,
                                p: 1.25,
                                borderRadius: "10px",
                                bgcolor: alpha(statusStyle.color, 0.08),
                                color: statusStyle.color,
                            }}>
                            {status === "pending" ?
                                <HourglassTopRoundedIcon sx={{ fontSize: 17, mt: "1px" }} />
                                : status === "joinWindow" ?
                                    <EventAvailableRoundedIcon sx={{ fontSize: 17, mt: "1px" }} />
                                    : <LockClockRoundedIcon sx={{ fontSize: 17, mt: "1px" }} />}
                            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
                                {statusNote}
                            </Typography>
                        </Stack>
                    )}

                    {countryNote && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{
                                mb: 1.25,
                                p: 1.25,
                                borderRadius: "10px",
                                bgcolor: alpha("#ff9800", 0.1),
                                color: "#b26a00",
                            }}>
                            <PublicRoundedIcon sx={{ fontSize: 17, mt: "1px" }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
                                {countryNote}
                            </Typography>
                        </Stack>
                    )}

                    {isMember ?
                        <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            endIcon={<ArrowForwardRoundedIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewButtonClick(_id);
                            }}
                            sx={{
                                py: 1.1,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontWeight: 700,
                                bgcolor: colors.primary,
                                "&:hover": { bgcolor: "#0a2159" },
                            }}>
                            View circle
                        </Button>
                        : <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            disabled={joinButtonDisabled}
                            endIcon={joinButtonDisabled ? undefined : <ArrowForwardRoundedIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onJoinButtonClick(_id);
                            }}
                            sx={{
                                py: 1.1,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontWeight: 700,
                                background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #17a37d 100%)`,
                                boxShadow: `0 8px 20px ${alpha(colors.mainSecondary, 0.3)}`,
                                "&:hover": {
                                    background: `linear-gradient(135deg, #25d6a6 0%, ${colors.mainSecondary} 100%)`,
                                },
                                "&.Mui-disabled": {
                                    background: alpha(colors.primary, 0.06),
                                    color: alpha(colors.primary, 0.4),
                                    boxShadow: "none",
                                },
                            }}>
                            {status === "pending" ?
                                "Request sent"
                                : status === "locked" ?
                                    "Joining opens soon"
                                    : "Request to join"}
                        </Button>
                    }
                </Box>
            </Card>
        </Grid>
    );
};

export default CircleDiscoveryCard;
